import { useState } from "react";
import { Check, ChevronDown, ChevronUp, SkipForward } from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  ContainerOrderLine,
  ContainerRouteStop,
} from "../../types/containerOrders";
import {
  completedStepCount,
  lineSteps,
  routeStopsOf,
  skippedStopsOf,
} from "../../utils/containerLineSteps";

/**
 * The rest of the route, as one line the driver can open.
 *
 * The frame above this shows the stop being worked and nothing else, which is
 * the right amount of screen for a phone held one-handed at a gate — but "what
 * am I doing after this" is a fair question to ask from the cab, and a screen
 * that cannot answer it sends the driver back to the office. So the whole route
 * is here, collapsed to a headline that says how far along the run is.
 *
 * Deliberately not the old stepper with the current card removed. This is a
 * *timeline* — a read-only account of a journey, past and future in one column
 * — and it carries no buttons at all. Every action lives in the frame, so there
 * is exactly one place on this screen where anything can be recorded, and no
 * way to confirm a step belonging to a stop the driver is not standing at.
 */

/** `12 Mar, 14:30` — when a stamp says something happened. */
const stamp = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/** `12 Mar · 09:00` — when a stop is booked for. */
const due = (stop: ContainerRouteStop) => {
  const date = stop.date ? new Date(stop.date) : null;
  const day =
    date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(undefined, { day: "numeric", month: "short" })
      : "";
  return [day, stop.time].filter(Boolean).join(" · ");
};

interface DriverRouteTimelineProps {
  line: ContainerOrderLine;
  /** Index into the full stop list of the stop the frame is showing. */
  currentStopIndex: number;
}

export function DriverRouteTimeline({
  line,
  currentStopIndex,
}: DriverRouteTimelineProps) {
  const { t } = useTranslation("containerOrders");

  // Open by default. It is the second thing on the screen and one line tall
  // when shut, so the cost of showing it is small — and a driver who has never
  // run this route has no way of knowing there is anything to open.
  const [open, setOpen] = useState(true);

  const stops = routeStopsOf(line);
  const steps = lineSteps(line);
  const done = completedStepCount(line);
  const skipped = skippedStopsOf(line);

  const active = stops
    .map((stop, index) => ({ stop, index }))
    .filter(({ stop }) => stop.enabled !== false);

  /**
   * How many stops are finished, and how many are not.
   *
   * Counted in *stops* rather than steps, because that is the unit the driver
   * thinks in — "three left" means three more places to drive to, where "six
   * steps left" means nothing anybody plans a day around. A stop is done when
   * both of its halves are stamped.
   */
  const stopIsDone = (index: number) => {
    const own = steps.filter((step) => step.stopIndex === index);
    return own.length > 0 && own.every((step) => steps.indexOf(step) < done);
  };

  const doneCount = active.filter(({ index }) => stopIsDone(index)).length;

  return (
    <div className="overflow-hidden rounded-[14px] border border-[#E4E2DA] bg-white">
      <button
        type="button"
        onClick={() => setOpen((state) => !state)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold text-[#1A1A18]">
            {t("driver.routeTimeline")}
          </span>
          {/* The headline answers the question the card exists for without
              being opened: how much of this run is behind me. */}
          <span className="mt-0.5 block text-[12px] text-[#6B6A64]">
            {t("driver.timelineProgress", {
              done: doneCount,
              left: active.length - doneCount,
            })}
          </span>
        </span>

        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-[#A6A49C]" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-[#A6A49C]" />
        )}
      </button>

      {open && (
        <ol className="space-y-0 px-3 pb-3">
          {active.map(({ stop, index }, position) => {
            const finished = stopIsDone(index);
            const current = index === currentStopIndex;
            const last = position === active.length - 1;

            const title =
              stop.role === "custom"
                ? stop.label?.trim() ||
                  stop.companyName?.trim() ||
                  t(`route.stopKinds.${stop.kind ?? "other"}`)
                : t(`route.${stop.role}`);

            return (
              <li key={String(stop._id)} className="flex gap-2.5">
                {/* The rail: a marker per stop and a line joining them, so the
                    column reads as one journey rather than as a list of rows.
                    The last stop has no line below it — a thread running off
                    the end suggests a stop that did not render. */}
                <div className="flex flex-col items-center">
                  <span
                    className={`flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${
                      finished
                        ? "bg-[#3B6D11] text-white"
                        : current
                          ? "bg-[#EF9F27] text-white"
                          : "border border-[#C9C7BE] bg-white text-[#A6A49C]"
                    }`}
                  >
                    {finished ? <Check className="h-2.5 w-2.5" /> : position + 1}
                  </span>
                  {!last && (
                    <span
                      className={`w-[1.5px] flex-1 ${
                        finished ? "bg-[#3B6D11]" : "bg-[#E4E2DA]"
                      }`}
                    />
                  )}
                </div>

                <div className={`min-w-0 flex-1 ${last ? "pb-0" : "pb-3"}`}>
                  <p
                    className={`truncate text-[13px] ${
                      current
                        ? "font-semibold text-[#1A1A18]"
                        : finished
                          ? "text-[#6B6A64]"
                          : "text-[#A6A49C]"
                    }`}
                  >
                    {title}
                  </p>
                  {/* One line under each, and which line depends on where the
                      stop sits in the run: a finished stop is asked when, a
                      current one what it is, and one ahead when it is due. */}
                  <p
                    className={`truncate text-[11.5px] ${
                      finished
                        ? "text-[#3B6D11]"
                        : current
                          ? "font-semibold text-[#854F0B]"
                          : "text-[#A6A49C]"
                    }`}
                  >
                    {finished
                      ? stamp(stop.completedAt ?? stop.arrivedAt)
                      : current
                        ? t("driver.timelineInProgress")
                        : due(stop) || t("driver.timelineUpcoming")}
                  </p>
                </div>
              </li>
            );
          })}

          {/* ── Stops that are not being made ────────────────────────────
              Listed rather than silently absent. A driver who has run this
              client's boxes twenty times knows the third stop is the terminal,
              and a route that simply stops after the loading site reads as a
              missing step or a broken app. "No drop-off — the box is staying on
              site" is the difference between an instruction and a gap, and the
              reason is exactly what dispatch typed. */}
          {skipped.map((stop: ContainerRouteStop) => (
            <li key={String(stop._id)} className="flex gap-2.5 pt-1">
              <div className="flex flex-col items-center">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-[#C9C7BE] bg-white">
                  <SkipForward className="h-3 w-3 text-[#A6A49C]" />
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] text-[#A6A49C] line-through">
                  {stop.role === "custom"
                    ? stop.label?.trim() ||
                      stop.companyName?.trim() ||
                      t(`route.stopKinds.${stop.kind ?? "other"}`)
                    : t(`route.${stop.role}`)}
                </p>
                <p className="whitespace-pre-wrap text-[11px] leading-snug text-[#6B6A64]">
                  {stop.skipReason?.trim() || t("driver.skippedNoReason")}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default DriverRouteTimeline;
